'use client'

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffEventWithWorker, StaffEventType, JobcardAppointment, AppointmentWithDetails } from '@garage/shared'
import { STAFF_EVENT_TYPE_LABELS } from '@garage/shared'
import { PageHeader, Button, IconButton, Chip, EmptyState, Skeleton, ChevronRightIcon, CarIcon, CalendarIcon } from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { appointmentJobcardUrl } from '@/lib/appointments.js'
import { useFeatures } from '@/lib/hooks.js'
import { StaffEventDialog } from '@/components/StaffEventDialog.js'
import { AppointmentDialog } from '@/components/AppointmentDialog.js'
import { DayDetailDialog } from '@/components/DayDetailDialog.js'
import { JOBCARD_STATUS_TONE } from '@/components/StatusChip.js'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const

/** "2026-09-05" from a local Date, never through toISOString (which is UTC). */
function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const EVENT_TONE: Record<StaffEventType, 'warning' | 'error' | 'success' | 'info' | 'teal'> = {
  late: 'warning',
  absent: 'error',
  holiday: 'success',
  other: 'info',
  overtime: 'teal',
}

const APPOINTMENT_TONE: Record<string, 'violet' | 'success' | 'error' | 'neutral'> = {
  scheduled: 'violet',
  arrived: 'success',
  no_show: 'error',
  cancelled: 'neutral',
}

/**
 * A day cell is a couple of centimetres wide — the full "Brand Model Year —
 * Plate" label used everywhere else in the app is always too long for it.
 * The plate is the thing staff actually recognise a car by in the yard, so
 * it stands in; the full label is still one click away, in the day's own
 * detail view.
 */
function shortCarLabel(appointment: Pick<JobcardAppointment, 'plate' | 'vin' | 'carLabel'>): string {
  if (appointment.plate) return appointment.plate
  if (appointment.vin) return appointment.vin
  return appointment.carLabel
}

/** Every date key from `from` to `to` inclusive, both "YYYY-MM-DD". */
function dateKeysBetween(from: string, to: string): string[] {
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  const cursor = new Date(fy!, fm! - 1, fd!)
  const end = new Date(ty!, tm! - 1, td!)
  const keys: string[] = []
  while (cursor <= end) {
    keys.push(toDateKey(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return keys
}

export function CalendarPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { appointmentsEnabled } = useFeatures()
  const today = useMemo(() => new Date(), [])
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())

  const [dialogDate, setDialogDate] = useState<string | null>(null)
  const [editingEvent, setEditingEvent] = useState<StaffEventWithWorker | undefined>()
  const [appointmentDialogDate, setAppointmentDialogDate] = useState<string | null>(null)
  const [editingAppointment, setEditingAppointment] = useState<AppointmentWithDetails | undefined>()
  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null)

  // 6-week grid (42 days) starting on the Sunday on/before the 1st of the month.
  const gridStart = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1)
    return new Date(viewYear, viewMonth, 1 - firstOfMonth.getDay())
  }, [viewYear, viewMonth])

  const gridDays = useMemo(
    () => Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)),
    [gridStart],
  )

  const from = toDateKey(gridDays[0]!)
  const to = toDateKey(gridDays[41]!)

  const { data, isLoading } = useQuery({
    queryKey: qk.staffEvents({ from, to }),
    queryFn: () => api.staffEvents.list({ from, to }),
  })

  const { data: bookedCarsData, isLoading: isBookedCarsLoading } = useQuery({
    queryKey: qk.jobcardAppointments({ from, to }),
    queryFn: () => api.jobcards.appointments({ from, to }),
  })

  const { data: appointmentsData, isLoading: isAppointmentsLoading } = useQuery({
    queryKey: qk.appointments({ from, to }),
    queryFn: () => api.appointments.list({ from, to }),
    enabled: appointmentsEnabled,
  })

  const eventsByDate = useMemo(() => {
    const map = new Map<string, StaffEventWithWorker[]>()
    for (const event of data?.items ?? []) {
      for (const dateKey of dateKeysBetween(event.date, event.endDate)) {
        const list = map.get(dateKey) ?? []
        list.push(event)
        map.set(dateKey, list)
      }
    }
    return map
  }, [data])

  /** Booked-in cars from jobcards, keyed by the appointment's date. */
  const bookedCarsByDate = useMemo(() => {
    const map = new Map<string, JobcardAppointment[]>()
    for (const appointment of bookedCarsData?.items ?? []) {
      const dateKey = appointment.appointmentAt.slice(0, 10)
      const list = map.get(dateKey) ?? []
      list.push(appointment)
      map.set(dateKey, list)
    }
    return map
  }, [bookedCarsData])

  /** Customer appointments, keyed by the appointment's date (YYYY-MM-DD). */
  const customerAppointmentsByDate = useMemo(() => {
    const map = new Map<string, AppointmentWithDetails[]>()
    for (const appointment of appointmentsData?.items ?? []) {
      const dateKey = appointment.date
      const list = map.get(dateKey) ?? []
      list.push(appointment)
      map.set(dateKey, list)
    }
    // Sort appointments by time within each date
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (!a.time && !b.time) return 0
        if (!a.time) return 1
        if (!b.time) return -1
        return a.time.localeCompare(b.time)
      })
    }
    return map
  }, [appointmentsData])

  const refetchEvents = () => {
    queryClient.invalidateQueries({ queryKey: ['staff-events'] })
    queryClient.invalidateQueries({ queryKey: ['appointments'] })
    queryClient.invalidateQueries({ queryKey: qk.dashboard })
  }

  const goToMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth + delta, 1)
    setViewYear(next.getFullYear())
    setViewMonth(next.getMonth())
  }

  const openAddDialog = (dateKey: string) => {
    setEditingEvent(undefined)
    setDialogDate(dateKey)
  }

  const openEditDialog = (event: StaffEventWithWorker) => {
    setEditingEvent(event)
    setDialogDate(event.date)
  }

  const closeDialog = () => {
    setDialogDate(null)
    setEditingEvent(undefined)
  }

  const openAddAppointmentDialog = (dateKey: string) => {
    setEditingAppointment(undefined)
    setAppointmentDialogDate(dateKey)
  }

  const openEditAppointmentDialog = (appointment: AppointmentWithDetails) => {
    setEditingAppointment(appointment)
    setAppointmentDialogDate(appointment.date)
  }

  const closeAppointmentDialog = () => {
    setAppointmentDialogDate(null)
    setEditingAppointment(undefined)
  }

  const openDayDetail = (dateKey: string) => {
    setDayDetailDate(dateKey)
  }

  const closeDayDetail = () => {
    setDayDetailDate(null)
  }

  const handleAddEventFromDayDetail = () => {
    const dateKey = dayDetailDate
    closeDayDetail()
    if (dateKey) openAddDialog(dateKey)
  }

  const handleSelectAppointment = (appointment: JobcardAppointment) => {
    closeDayDetail()
    navigate(`/jobcards/${appointment.id}`)
  }

  const handleSelectEvent = (event: StaffEventWithWorker) => {
    closeDayDetail()
    openEditDialog(event)
  }

  const handleCreateJobcard = (appointment: AppointmentWithDetails) => {
    closeDayDetail()
    navigate(appointmentJobcardUrl(appointment))
  }

  const todayKey = toDateKey(today)
  const hasAnyEvents = (data?.items.length ?? 0) > 0 || (bookedCarsData?.items.length ?? 0) > 0 || (appointmentsData?.items.length ?? 0) > 0
  const loading = isLoading || isBookedCarsLoading || isAppointmentsLoading

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('calendar.title')}
        subtitle={appointmentsEnabled ? t('calendar.subtitle') : t('calendar.subtitleWithoutAppointments')}
        actions={
          <div className="flex gap-2">
            {appointmentsEnabled && (
              <Button variant="filled" onClick={() => openAddAppointmentDialog(todayKey)}>
                {t('appointments.addAppointment')}
              </Button>
            )}
            <Button variant="outlined" onClick={() => openAddDialog(todayKey)}>
              {t('calendar.addEvent')}
            </Button>
          </div>
        }
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton
            icon={<ChevronRightIcon className="w-5 h-5 rotate-180 rtl:rotate-0" />}
            label={t('calendar.previousMonth')}
            variant="standard"
            onClick={() => goToMonth(-1)}
          />
          <h2 className="text-title-md text-on-surface w-48 text-center">
            {t(`calendar.months.${MONTH_KEYS[viewMonth]}`)} {viewYear}
          </h2>
          <IconButton
            icon={<ChevronRightIcon className="w-5 h-5 rtl:rotate-180" />}
            label={t('calendar.nextMonth')}
            variant="standard"
            onClick={() => goToMonth(1)}
          />
        </div>
        <Button variant="text" onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()) }}>
          {t('calendar.today')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-on-surface-variant">
        {appointmentsEnabled && (
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet" /> {t('appointments.legend')}</span>
        )}
        <span className="flex items-center gap-1.5"><CarIcon className="w-3.5 h-3.5" /> {t('calendar.legend.carBooked')}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning" /> {t('calendar.legend.late')}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-error" /> {t('calendar.legend.absent')}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success" /> {t('calendar.legend.onLeave')}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-teal" /> {t('calendar.legend.overtime')}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-info" /> {t('calendar.legend.other')}</span>
      </div>

      {loading ? (
        <Skeleton lines={6} />
      ) : (
        <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
          <div className="grid grid-cols-7 border-b border-outline-variant">
            {WEEKDAY_KEYS.map((key) => (
              <div key={key} className="px-2 py-2 text-label-md text-on-surface-variant text-center">
                {t(`calendar.weekdays.${key}`)}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {gridDays.map((day) => {
              const dateKey = toDateKey(day)
              const inMonth = day.getMonth() === viewMonth
              const isToday = dateKey === todayKey
              const dayEvents = eventsByDate.get(dateKey) ?? []
              const dayBookedCars = bookedCarsByDate.get(dateKey) ?? []
              const dayCustomerAppointments = customerAppointmentsByDate.get(dateKey) ?? []

              return (
                // A plain div, not a button: it holds real buttons (the event
                // chips) inside it, and a button cannot nest another button.
                <div
                  key={dateKey}
                  onClick={() => openDayDetail(dateKey)}
                  className={`
                    min-h-24 p-2 text-start border-b border-e border-outline-variant last:border-e-0
                    interactive hover:bg-surface-high align-top cursor-pointer
                    ${inMonth ? '' : 'opacity-40'}
                  `}
                >
                  <div className={`text-body-sm mb-1 ${isToday ? 'inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-on-primary' : 'text-on-surface-variant'}`}>
                    {day.getDate()}
                  </div>
                  <div className="flex flex-col gap-1 items-start w-full min-w-0">
                    {/* Customer appointments first, sorted by time */}
                    {appointmentsEnabled && dayCustomerAppointments.map((appointment) => (
                      <div key={appointment.id} onClick={(e) => e.stopPropagation()} className="w-full min-w-0">
                        <Chip
                          tone={APPOINTMENT_TONE[appointment.status] as any}
                          icon={<CalendarIcon className="w-3 h-3 flex-shrink-0" />}
                          label={
                            appointment.time
                              ? `${appointment.time} ${appointment.customerLabel}`
                              : appointment.customerLabel
                          }
                          onClick={() => openEditAppointmentDialog(appointment)}
                          className="!px-2 !py-0.5 text-[11px] !flex w-full min-w-0 [&>span]:truncate [&>span]:min-w-0"
                        />
                      </div>
                    ))}
                    {/* Booked-in cars */}
                    {dayBookedCars.map((appointment) => (
                      <div key={appointment.id} onClick={(e) => e.stopPropagation()} className="w-full min-w-0">
                        <Chip
                          tone={JOBCARD_STATUS_TONE[appointment.status]}
                          icon={<CarIcon className="w-3 h-3 flex-shrink-0" />}
                          label={shortCarLabel(appointment)}
                          onClick={() => navigate(`/jobcards/${appointment.id}`)}
                          className="!px-2 !py-0.5 text-[11px] !flex w-full min-w-0 [&>span]:truncate [&>span]:min-w-0"
                        />
                      </div>
                    ))}
                    {/* Staff events */}
                    {dayEvents.map((event) => (
                      <div key={event.id} onClick={(e) => e.stopPropagation()} className="w-full min-w-0">
                        <Chip
                          tone={EVENT_TONE[event.type]}
                          label={event.workerName ?? t(`calendar.eventTypes.${event.type}`, STAFF_EVENT_TYPE_LABELS[event.type])}
                          onClick={() => openEditDialog(event)}
                          className="!px-2 !py-0.5 text-[11px] !flex w-full min-w-0 [&>span]:truncate [&>span]:min-w-0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {!loading && !hasAnyEvents && (
        <EmptyState
          title={t('calendar.nothingThisMonth')}
          description={t('calendar.nothingThisMonthDescription')}
        />
      )}

      {dialogDate && (
        <StaffEventDialog
          open={!!dialogDate}
          date={dialogDate}
          event={editingEvent}
          onClose={closeDialog}
          onSaved={refetchEvents}
        />
      )}

      {appointmentDialogDate && (
        <AppointmentDialog
          open={!!appointmentDialogDate}
          date={appointmentDialogDate}
          appointment={editingAppointment}
          onClose={closeAppointmentDialog}
          onSaved={refetchEvents}
        />
      )}

      <DayDetailDialog
        open={!!dayDetailDate}
        dateKey={dayDetailDate}
        bookedCars={dayDetailDate ? bookedCarsByDate.get(dayDetailDate) ?? [] : []}
        customerAppointments={dayDetailDate ? customerAppointmentsByDate.get(dayDetailDate) ?? [] : []}
        events={dayDetailDate ? eventsByDate.get(dayDetailDate) ?? [] : []}
        onClose={closeDayDetail}
        onAddEvent={handleAddEventFromDayDetail}
        onAddAppointment={() => {
          closeDayDetail()
          if (dayDetailDate) openAddAppointmentDialog(dayDetailDate)
        }}
        onSelectAppointment={handleSelectAppointment}
        onSelectEvent={handleSelectEvent}
        onSelectAppointmentForEdit={(appointment) => {
          closeDayDetail()
          openEditAppointmentDialog(appointment)
        }}
        onCreateJobcard={handleCreateJobcard}
      />
    </div>
  )
}
